import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, User, Key, ChevronDown, Camera, Trash2, Eye } from "lucide-react";
import { api } from "../../lib/api";
import ProfilePhotoCropModal from "./ProfilePhotoCropModal";

/**
 * Header profile menu — shows the signed-in admin, exposes change-password
 * and sign-out. Falls back to a subtle "shared secret" label when the current
 * session is via X-Admin-Secret rather than a real admin_users record.
 */
export default function AdminProfileMenu({ onLogout }) {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin-auth/me").then(({ data }) => {
      setMe(data);
      setPhoneDraft(data.phone || "");
      setTitleDraft(data.title || "");
      setNameDraft(data.display_name || "");
      setEmailDraft(data.email || "");
    }).catch(() => setMe(null));
  }, []);

  // Close on outside click.
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowPw(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (!cur || !next || next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin-auth/change-password",
        { current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur(""); setNext(""); setShowPw(false); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin-auth/me/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMe(data);
      toast.success("Photo updated — will show on every PDF cover from now on.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ---- Crop-before-upload flow ----
  // 1) User picks a file → we read it as a data URL and open the crop modal
  // 2) User pans / zooms until happy → confirm returns a cropped JPEG blob
  // 3) That blob is uploaded via uploadPhoto (server does final resize to 360×360)
  const [cropSrc, setCropSrc] = useState(null);

  const handleFilePicked = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image too large (20MB max)."); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCropConfirm = async (croppedFile) => {
    await uploadPhoto(croppedFile);
    setCropSrc(null);
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your cover-page photo?")) return;
    setPhotoBusy(true);
    try {
      const { data } = await api.delete("/admin-auth/me/photo");
      setMe(data);
      toast.success("Photo removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileBusy(true);
    try {
      const patch = {
        phone: phoneDraft || null,
        title: titleDraft || null,
      };
      if (nameDraft.trim()) patch.display_name = nameDraft.trim();
      if (emailDraft.trim()) patch.email = emailDraft.trim();
      const { data } = await api.patch("/admin-auth/me/profile", patch);
      setMe(data);
      setNameDraft(data.display_name || "");
      setEmailDraft(data.email || "");
      setShowProfile(false);
      toast.success("Profile saved — updated on every future PDF cover.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setProfileBusy(false);
    }
  };

  const [previewBusy, setPreviewBusy] = useState(false);
  const previewCover = async () => {
    setPreviewBusy(true);
    try {
      const res = await api.get("/admin-auth/me/cover-preview.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      // Revoke a bit later so the tab has time to load.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview.");
    } finally {
      setPreviewBusy(false);
    }
  };

  // ---- Inline cover thumbnail: rasterized mini preview, no new tab needed ----
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbLoading, setThumbLoading] = useState(false);
  useEffect(() => {
    if (!open || !me) return;
    let revoked = null;
    setThumbLoading(true);
    api.get("/admin-auth/me/cover-thumb.png", { responseType: "blob" })
      .then((res) => {
        revoked = window.URL.createObjectURL(res.data);
        setThumbUrl(revoked);
      })
      .catch(() => setThumbUrl(null))
      .finally(() => setThumbLoading(false));
    return () => { if (revoked) window.URL.revokeObjectURL(revoked); };
  }, [open, me]);

  const displayLabel = me ? (me.display_name || me.username) : "Founder key";
  const roleLabel = me?.role === "founder" ? "Founder" : me?.role === "member" ? "Staff" : "Shared secret";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="admin-profile-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#1e3d3a]/60 hover:text-[#c9a961] transition-colors duration-300"
      >
        <User className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{displayLabel}</span>
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="admin-profile-menu"
          className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-[#1e3d3a]/12 bg-white shadow-xl z-40 p-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200"
        >
          <div className="border-b border-[#1e3d3a]/8 pb-3">
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                {me?.photo_data_url ? (
                  <img
                    data-testid="profile-photo"
                    src={me.photo_data_url}
                    alt={displayLabel}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-[#c9a961]/60"
                  />
                ) : (
                  <div
                    data-testid="profile-photo-placeholder"
                    className="h-12 w-12 rounded-full bg-[#1e3d3a] text-[#c9a961] flex items-center justify-center font-head text-lg ring-2 ring-[#c9a961]/60"
                  >
                    {(displayLabel || "E").slice(0, 1).toUpperCase()}
                  </div>
                )}
                {me && (
                  <button
                    type="button"
                    data-testid="profile-photo-upload"
                    onClick={() => fileRef.current?.click()}
                    disabled={photoBusy}
                    title="Upload cover-page photo"
                    className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-[#a3823f] text-white flex items-center justify-center hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
                  >
                    {photoBusy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Camera className="h-2.5 w-2.5" />}
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFilePicked(e.target.files?.[0])}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1e3d3a] truncate">{displayLabel}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">{me?.title || roleLabel}</p>
                {me?.email && <p className="text-[10px] text-[#1e3d3a]/50 truncate mt-0.5">{me.email}</p>}
              </div>
            </div>
            {me?.photo_data_url && (
              <button
                type="button"
                data-testid="profile-photo-remove"
                onClick={removePhoto}
                disabled={photoBusy}
                className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/45 hover:text-[#b23a48] inline-flex items-center gap-1 transition-colors duration-300"
              >
                <Trash2 className="h-2.5 w-2.5" /> Remove photo
              </button>
            )}
            {me && (
              <div className="mt-3">
                <p className="text-[9px] uppercase tracking-[0.24em] text-[#a3823f] mb-1.5 flex items-center gap-1">
                  <Eye className="h-2.5 w-2.5" /> Cover page · click for full PDF
                </p>
                {thumbLoading && !thumbUrl && (
                  <div data-testid="cover-thumb-loading" className="h-40 rounded-lg border border-[#1e3d3a]/10 bg-[#eae7de]/50 grid place-items-center">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[#a3823f]" />
                  </div>
                )}
                {thumbUrl && (
                  <button
                    type="button"
                    data-testid="cover-thumb"
                    onClick={previewCover}
                    disabled={previewBusy}
                    title="Open the full-size cover PDF"
                    className="block w-full rounded-lg border border-[#1e3d3a]/12 overflow-hidden hover:border-[#c9a961] transition-colors duration-300 disabled:opacity-60"
                  >
                    <img
                      src={thumbUrl}
                      alt="Cover page preview"
                      className="w-full object-contain bg-white"
                    />
                  </button>
                )}
                {!thumbLoading && !thumbUrl && (
                  <button
                    type="button"
                    data-testid="profile-cover-preview"
                    onClick={previewCover}
                    disabled={previewBusy}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] hover:text-[#1e3d3a] inline-flex items-center gap-1 transition-colors duration-300 disabled:opacity-60"
                  >
                    {previewBusy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Eye className="h-2.5 w-2.5" />}
                    Preview cover page
                  </button>
                )}
              </div>
            )}
          </div>

          {me ? (
            !showPw ? (
              <>
                {!showProfile ? (
                  <button
                    type="button"
                    data-testid="admin-profile-edit-contact"
                    onClick={() => setShowProfile(true)}
                    className="w-full flex items-center gap-2 text-xs text-[#1e3d3a] hover:bg-[#c9a961]/10 rounded-md px-2 py-1.5 transition-colors duration-300"
                  >
                    <User className="h-3.5 w-3.5 text-[#a3823f]" /> Edit name · email · contact · title
                  </button>
                ) : (
                  <form onSubmit={saveProfile} className="space-y-1.5">
                    <div>
                      <label className="block text-[9px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Name</label>
                      <input
                        data-testid="profile-name"
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        placeholder="Your name (shows on PDF cover)"
                        maxLength={60}
                        className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Email</label>
                      <input
                        data-testid="profile-email"
                        type="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        placeholder="you@elevaresolutions.me"
                        maxLength={120}
                        className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                      />
                    </div>
                    <input
                      data-testid="profile-title"
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      placeholder="Title (shows on PDF cover)"
                      maxLength={120}
                      className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                    />
                    <input
                      data-testid="profile-phone"
                      type="tel"
                      value={phoneDraft}
                      onChange={(e) => setPhoneDraft(e.target.value)}
                      placeholder="Direct phone (e.g. (260) 200-0907)"
                      maxLength={40}
                      className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                    />
                    <div className="flex items-center gap-1.5">
                      <button
                        type="submit"
                        data-testid="profile-save"
                        disabled={profileBusy}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                      >
                        {profileBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <User className="h-3 w-3" />}
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowProfile(false); setPhoneDraft(me?.phone || ""); setTitleDraft(me?.title || ""); setNameDraft(me?.display_name || ""); setEmailDraft(me?.email || ""); }}
                        className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-2 py-1 transition-colors duration-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                <button
                  type="button"
                  data-testid="admin-profile-change-pw"
                  onClick={() => setShowPw(true)}
                  className="w-full flex items-center gap-2 text-xs text-[#1e3d3a] hover:bg-[#c9a961]/10 rounded-md px-2 py-1.5 transition-colors duration-300"
                >
                  <Key className="h-3.5 w-3.5 text-[#a3823f]" /> Change password
                </button>
              </>
            ) : (
              <form onSubmit={changePassword} className="space-y-1.5">
                <input
                  data-testid="pw-current" type="password" value={cur}
                  onChange={(e) => setCur(e.target.value)}
                  placeholder="Current password"
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                />
                <input
                  data-testid="pw-new" type="password" value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder="New password (6+ chars)"
                  className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-[#eae7de] px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="submit" data-testid="pw-save"
                    disabled={busy}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Key className="h-3 w-3" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPw(false); setCur(""); setNext(""); }}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-2 py-1 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )
          ) : (
            <p className="text-[11px] text-[#1e3d3a]/55 px-2 py-1.5">
              You're signed in with the shared founder key — create a personal account at the login page to unlock password changes.
            </p>
          )}

          <button
            type="button"
            data-testid="admin-profile-logout"
            onClick={async () => {
              try { await api.post("/admin-auth/logout"); } catch {}
              onLogout?.();
            }}
            className="w-full flex items-center gap-2 text-xs text-[#b23a48] hover:bg-[#b23a48]/8 rounded-md px-2 py-1.5 transition-colors duration-300"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out
          </button>
        </div>
      )}
      {cropSrc && (
        <ProfilePhotoCropModal
          imageSrc={cropSrc}
          busy={photoBusy}
          onConfirm={handleCropConfirm}
          onClose={() => setCropSrc(null)}
        />
      )}
    </div>
  );
}
